/*
 * ШЕСТЬ ГЛАГОЛОВ ВМЕСТО ВОСЕМНАДЦАТИ РОДОВ.
 *
 * Замер ДО (docs/UI_TARGET_2026.md §7.3, 2026-09-13): 18 групп на 96 команд —
 * в среднем пять команд на группу, и группы называли РАЗНОЕ. Часть — род
 * поверхности («Панели», «Режимы сцены», «Рабочие места»), часть — род занятия
 * («Вид», «Свет», «Выбор»), часть — подсистему («АРМ Технолог», «Кухня по
 * изображениям»). Это наслоение, а не система: четыре из них («Вид», «Вид и
 * камера», «Режим», «Свет») называли ОДНО И ТО ЖЕ — как показать модель, — и
 * человек, ищущий «как показать», получал четыре заголовка вместо одного.
 *
 * Целевая группировка — по ГЛАГОЛУ, как у PRO100 7 (Файл / Правка / Вид /
 * Элемент / Инструменты). Причина не в красоте: человек ищет по тому, ЧТО ОН
 * ХОЧЕТ СДЕЛАТЬ, а не по тому, в какой панели это нарисовано. Шестым добавлен
 * «Выпуск» — у мебельного продукта есть то, чего нет у редактора: результат,
 * который уходит наружу (смета, чертежи, заказ), и проверка, которая этому
 * мешает.
 *
 * ГРУППА ЗАДАЁТСЯ ЗДЕСЬ, А НЕ В 96 МЕСТАХ ВЫЗОВА. Строка, вписанная рядом с
 * командой, — ровно тот способ, каким набралось восемнадцать групп: каждый
 * новый экран приносил свою. Таблица ниже — единственный источник, и её полнота
 * сторожится пином.
 */

/** Шесть глаголов. Порядок — порядок заголовков в палитре и на экране «Все». */
export const COMMAND_GROUPS = [
  'Файл',
  'Правка',
  'Вид',
  'Элемент',
  'Инструменты',
  'Выпуск',
] as const;

export type CommandGroup = (typeof COMMAND_GROUPS)[number];

/**
 * Метка для команды, которой группа не назначена.
 *
 * Она НАЗВАНА и видна на экране, а не подставлена тихо в «Инструменты».
 * Молчаливое умолчание — это способ не узнать, что таблица отстала от реестра;
 * тот же приём уже стоит у этапов (`UNASSIGNED_STAGE_LABEL`). В рабочем дереве
 * этот заголовок обязан не появляться — за этим следит пин.
 */
export const UNGROUPED_COMMAND_LABEL = 'Без группы — глагол не назначен';

/** Чем каждый глагол отвечает на вопрос «что я хочу сделать». */
export const COMMAND_GROUP_MEANING: Readonly<Record<CommandGroup, string>> = {
  'Файл': 'Открыть проект, принести и унести данные, выйти',
  'Правка': 'Изменить сцену: отменить, вернуть, поставить и переставить',
  'Вид': 'Посмотреть иначе, ничего в модели не меняя',
  'Элемент': 'Сделать что-то с выделенным объектом',
  'Инструменты': 'Орудия со своим экраном и своим ходом работы',
  'Выпуск': 'Проверить и выдать: смета, чертежи, документы, заказ',
};

/*
 * Таблица «команда → глагол». Разложена по глаголам, а не по алфавиту: так
 * видно состав каждой группы целиком и сразу заметно, если в «Вид» попало то,
 * что модель меняет.
 */
const MEMBERS: Readonly<Record<CommandGroup, readonly string[]>> = {
  // Проект как файл: где я, что открыто, что принести и унести.
  'Файл': [
    'workspace.scene', 'workspace.tech', 'workspace.orders', 'workspace.models',
    'app.recognition', 'app.mesh-studio', 'app.event-location', 'app.buildings',
    'project.model-picker', 'project.menu', 'project.share',
    'project.export', 'project.import', 'project.import-source',
    'project.diagnostics',
    'app.locale', 'app.tour', 'app.command-palette', 'app.shortcuts', 'app.logout',
    'leftRail.models', 'leftRail.ingest', 'panel-ing',
  ],
  // Меняет сцену, но не привязано к одному выделенному объекту.
  'Правка': [
    'history.undo', 'history.redo',
    'leftRail.kitchenConstructor', 'leftRail.layoutTools',
    'leftRail.moduleLibrary', 'leftRail.catalog', 'leftRail.cabinetTemplates',
    'sceneMode.params',
  ],
  /*
   * Сюда собраны четыре прежние группы — «Вид», «Вид и камера», «Режим»,
   * «Свет» — плюс «Выбор». Все они отвечают на один вопрос: КАК ПОКАЗАНО.
   * Скрытие и изоляция тоже здесь, а не в «Правке»: по §7.8 видимость не
   * идёт в отмену, потому что модель от неё не меняется.
   */
  'Вид': [
    'view-persp', 'view-iso', 'view-top', 'view-front', 'view-side',
    'mode-solid', 'mode-wire', 'mode-xray',
    'light-studio', 'light-show', 'light-day', 'light-photo',
    'dim-off', 'dim-on', 'dim-inline', 'explode-on', 'explode-off', 'section',
    'theme', 'app.appearance',
    'viewport.projection', 'viewport.layers', 'viewport.dimensions', 'viewport.environment',
    'selection.hide', 'selection.isolate', 'selection.show-all',
    'panels.open-all', 'panels.close-all',
    'sceneMode.scene', 'sceneMode.plan', 'sceneMode.modules',
  ],
  // Всё, что говорит о ВЫДЕЛЕННОМ объекте или правит именно его.
  'Элемент': [
    'rightRail.properties', 'rightRail.furnitureList', 'rightRail.annotations',
    'leftRail.structure', 'panel-struct',
    'leftRail.electrical',
    'leftRail.fill', 'panel-fill', 'module.actions',
    'leftRail.materials', 'panel-mat', 'leftRail.hardware',
  ],
  // Орудия: у каждого свой экран, свой ход работы и свой результат.
  'Инструменты': [
    'image-kitchen.upload', 'image-kitchen.graph1', 'image-kitchen.graph2',
    'image-kitchen.model', 'image-kitchen.smeta',
    'techSection.cut', 'techSection.operations', 'techSection.production',
    'techSection.materials', 'techSection.advisor',
    'rightRail.knowledge', 'rightRail.chat',
  ],
  // То, что уходит наружу, и то, что этому мешает.
  'Выпуск': [
    'sceneMode.analysis', 'sceneMode.verify', 'sceneMode.estimate', 'sceneMode.drawings',
    'rightRail.analysis', 'panel-anal',
    'rightRail.estimate', 'panel-est',
    'rightRail.history', 'panel-hist',
    'rightRail.renders', 'render',
    'techSection.smeta', 'techSection.briefs', 'techSection.revisions',
    'techSection.review', 'techSection.library-review', 'techSection.pricebook',
    'leftRail.orders',
  ],
};

const GROUP_BY_COMMAND_ID: ReadonlyMap<string, CommandGroup> = new Map(
  (Object.entries(MEMBERS) as [CommandGroup, readonly string[]][])
    .flatMap(([group, ids]) => ids.map((id) => [id, group] as const)),
);

/** Идентификаторы, которым глагол назначен. Нужен пинам и замерам. */
export const GROUPED_COMMAND_IDS: readonly string[] = [...GROUP_BY_COMMAND_ID.keys()];

/**
 * Глагол команды. Неизвестный идентификатор получает НАЗВАННЫЙ отказ, а не
 * тихое умолчание: заголовок «Без группы» виден человеку и виден пину.
 */
export function groupOfCommand(commandId: string): string {
  return GROUP_BY_COMMAND_ID.get(commandId) ?? UNGROUPED_COMMAND_LABEL;
}

/**
 * Разложить команды по глаголам В ОБЪЯВЛЕННОМ ПОРЯДКЕ.
 *
 * До этого и палитра, и экран «Все» раскладывали группы в порядке первой
 * встречи в реестре: заголовки переезжали, стоило человеку набрать запрос, —
 * порядок задавал не смысл, а то, какая команда попалась первой. Постоянство
 * позиции — то же правило, ради которого у Fusion секторы радиального меню не
 * настраиваются (§7.2): мышечная память дороже полноты.
 *
 * Пустые группы выпадают: заголовок, под которым ничего нет, — шум. Команда с
 * незнакомым глаголом попадает в НАЗВАННУЮ группу в конце, а не исчезает.
 */
export function groupCommandsByVerb<T extends { group: string }>(
  commands: readonly T[],
): ReadonlyArray<readonly [string, readonly T[]]> {
  const buckets = new Map<string, T[]>();
  for (const command of commands) {
    const bucket = buckets.get(command.group);
    if (bucket) bucket.push(command);
    else buckets.set(command.group, [command]);
  }
  const ordered: Array<readonly [string, readonly T[]]> = [];
  for (const group of COMMAND_GROUPS) {
    const members = buckets.get(group);
    if (members?.length) ordered.push([group, members]);
    buckets.delete(group);
  }
  // Всё, чего нет среди шести глаголов, — видно и названо, а не потеряно.
  for (const [group, members] of buckets) {
    if (members.length) ordered.push([group, members]);
  }
  return ordered;
}
